/**
 * The box as the arm's wrist camera has mapped it, in 3-D: drag to turn it, scroll to zoom,
 * right-drag to move. The map fills in as the arm is used -- every wrist frame goes into it --
 * and the camera mount is drawn where it is, in the colour the arm gives it against the box.
 */
import { useEffect, useRef, useState } from "react";
import { Color, PerspectiveCamera, Scene, WebGLRenderer } from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";

import type { BoxMap, ToolModel } from "../adamo/boxmap";
import type { StatusFeed } from "../adamo/status";
import type { PathState } from "../adamo/types";
import { BoxMapView } from "../boxmap/view";

type Rig = { view: BoxMapView; camera: PerspectiveCamera; controls: OrbitControls };

export function BoxMapPanel({ map, tool, feed }: { map: BoxMap | null; tool: ToolModel | null; feed: StatusFeed }) {
  const host = useRef<HTMLDivElement>(null);
  const rig = useRef<Rig | null>(null);
  const framed = useRef(false);
  const [, tick] = useState(0);

  useEffect(() => {
    const el = host.current;
    if (!el) return;
    const renderer = new WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(window.devicePixelRatio);
    el.appendChild(renderer.domElement);
    const scene = new Scene();
    scene.background = new Color(0x0b0d10);
    const camera = new PerspectiveCamera(45, 1, 0.01, 20);
    const view = new BoxMapView({ grid: true });
    scene.add(view.group);
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    rig.current = { view, camera, controls };
    frame(rig.current);

    const resize = () => {
      const w = el.clientWidth, h = el.clientHeight;
      renderer.setSize(w, h, false);
      camera.aspect = w / Math.max(h, 1);
      camera.updateProjectionMatrix();
    };
    const observer = new ResizeObserver(resize);
    observer.observe(el);
    resize();

    let raf = 0;
    const loop = () => {
      view.setStatus(feed.current());
      controls.update();
      renderer.render(scene, camera);
      raf = requestAnimationFrame(loop);
    };
    loop();
    const clock = window.setInterval(() => tick((n) => n + 1), 1000);
    return () => {
      cancelAnimationFrame(raf);
      window.clearInterval(clock);
      observer.disconnect();
      controls.dispose();
      view.dispose();
      renderer.dispose();
      el.removeChild(renderer.domElement);
      rig.current = null;
    };
  }, [feed]);

  useEffect(() => {
    if (!map || !rig.current) return;
    rig.current.view.setMap(map);
    if (!framed.current) { frame(rig.current); framed.current = true; }
  }, [map]);

  useEffect(() => {
    if (tool && rig.current) rig.current.view.setToolModel(tool);
  }, [tool]);

  const rim = feed.current()?.rim ?? null;
  const age = map ? Math.round((performance.now() - map.receivedAt) / 1000) : null;
  return (
    <div className="boxmap">
      <div className="boxmap-view" ref={host} />
      <div className="boxmap-legend">
        <span>{map ? `${map.n.toLocaleString()} voxels · ${map.frames.toLocaleString()} frames · ${age} s ago`
                   : "nothing yet: the arm sends its map once its wrist camera has seen something"}</span>
        <span className="ramp" title="height above the bench">bench <i /> 15 cm</span>
        <span className="taught" title="start, waypoints, end: the way the trip goes; fainter while teaching = where the next waypoint would go">
          <i /> path {pathSummary(feed.current()?.taught)}</span>
        <span>mount: <b className={`lvl ${rim?.level ?? "clear"}`}>{rim?.level ?? "—"}</b>
          {rim?.clear_mm != null && ` · ${Math.max(rim.clear_mm, 0)} mm${rim.which ? ` (${rim.which})` : ""}`}</span>
        <button onClick={() => rig.current && frame(rig.current)}>Reset view</button>
      </div>
    </div>
  );
}

function pathSummary(t: PathState | undefined): string {
  if (!t?.start) return "not taught";
  return `S → ${t.waypoints ? `${t.waypoints} waypoint${t.waypoints === 1 ? "" : "s"} → ` : ""}${t.end ? "E" : "(no end yet)"}`;
}

/** From behind and above the arm's base, looking at the box. */
function frame({ view, camera, controls }: Rig): void {
  const f = view.focus;
  camera.position.set(f.x, f.y + 0.45, f.z + 0.55);
  controls.target.copy(f);
  controls.update();
}
